import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EnvironmentService } from './environment.service';
import { BehaviorSubject, Observable, of, } from 'rxjs';
import { map } from 'rxjs/operators';
import { AddName } from '../models/notes/add-name.model';
import { AddNameContainer } from '../add-note/add-name/add-name';
import { ChooseSongComponent } from '../add-note/choose-song/choose-song.component';
import { AddThoughtsContainer } from '../add-note/add-thoughts/add-thoughts';
import { PainMeterContainer } from '../add-note/pain-meter/pain-meter';
import { RandomQuestionComponent } from '../add-note/random-question/random-question.component';
import { DescribeSecretionContainer } from '../add-note/describe-secretion/describe-secretion';
import { ReportCardComponent } from '../add-note/report-card/report-card.component';
import { HttpHelper } from '../helpers/http';
import { LocalStorageHelper } from '../helpers/local-storage';
import { SignInService } from './sign-in.service';
import { GetNoteResponse } from '../models/notes/get-note-response.model';
import { CreateNoteRequest } from '../models/notes/create-note-request.model';
import { Rating } from '../models/notes/rating';
import { Answer } from '../models/notes/answer.model';
import { CreateNoteResponse } from '../models/notes/create-note-response.model';
import { GetSongsResponse } from '../models/notes/get-songs-response.model';
import { Song } from '../interfaces/song.interface';
import { Notes, Raw, ReadableNote } from '../interfaces/view-notes.interface';
import { WriteableNote } from '../interfaces/writeable-note.interface';
import { Router } from '@angular/router';
import { NavigationHelper } from '../helpers/navigation';
import { PottyNotesConstants } from '../potty-notes-consts';

@Injectable({
	providedIn: 'root',
})
export class NoteService {
	private diaryOwnerNotesSubject = new BehaviorSubject<Notes>({} as Notes);
	private notesControllerBasePath: string;
	private cachedSongs: Song[] = [];
	public cachedQuestions: any = null;
	public selectedSong: Song | null = null;
	public painMeterEmojiFileName: string | null = null;
	public selectedHero: string | null = null;
	public selectedSecretionImg: string | null = null;
	public thoughts: string | null = null;
	public selectedQuestionAnswer: string | null = null;
	public selectedQuestionIndex: number | null = null;
	public selectedQuestionId: string | null = null;
	public reportCardRatings: Rating[] = [];
	public totalReportCardRating: number = 0;
	public reportCardScrollPosition: number = 0;

	constructor(private http: HttpClient,
				private environmentService: EnvironmentService,
				private httpHelper: HttpHelper,
				private localStorageHelper: LocalStorageHelper,
				private signInService: SignInService,
				private navigationHelper: NavigationHelper) {
					
		this.notesControllerBasePath = this.environmentService.getBaseUrl() + '/api/note';
		this.reportCardRatings = [
			new Rating("Ambiance", PottyNotesConstants.ambianceOne, 0),
			new Rating("Cleanliness", PottyNotesConstants.cleanlinessOne, 0),
			new Rating("Toilet Paper", PottyNotesConstants.toiletPaperOne, 0),
			new Rating("Privacy", PottyNotesConstants.privacyOne, 0),
			new Rating("Comfort", PottyNotesConstants.comfortOne, 0),
			new Rating("Overall Rating", null, 0)
		];
	}

	public startNote(router: Router, contextualDiaryId: string) {
		this.clearNoteRequestData();
		this.localStorageHelper.setCurrentDiaryForCurrentNote(contextualDiaryId);
		router.navigate([`${this.navigationHelper.navigateToStartOfFlow()}`]);
	}

	public setAddName(name: AddName, isSkipped: boolean) {
		let newSegment = { value: name, componentIdentifier: AddNameContainer.ComponentIdentifier, isSkipped } as WriteableNote;
		this.saveNoteState(name, AddNameContainer.ComponentIdentifier, newSegment);
	}

	public setThoughts(thoughts: string, isSkipped: boolean) {
		let newSegment = { value: thoughts, componentIdentifier: AddThoughtsContainer.ComponentIdentifier, isSkipped } as WriteableNote;
		this.saveNoteState(thoughts, AddThoughtsContainer.ComponentIdentifier, newSegment);
	}

	public setSong(song: Song | undefined, isSkipped: boolean) {
		let newSegment = { value: song, componentIdentifier: ChooseSongComponent.ComponentIdentifier, isSkipped } as WriteableNote;
		this.saveNoteState(song, ChooseSongComponent.ComponentIdentifier, newSegment);
	}

	public setPainMeterEmojiPath(emojiPath: string, isSkipped: boolean) {
		let newSegment = { value: emojiPath, componentIdentifier: PainMeterContainer.ComponentIdentifier, isSkipped } as WriteableNote;
		this.saveNoteState(emojiPath, PainMeterContainer.ComponentIdentifier, newSegment);
	}

	public setAnswerForQuestion(answer: Answer, isSkipped: boolean) {
		let newSegment = { value: answer, componentIdentifier: RandomQuestionComponent.ComponentIdentifier, isSkipped } as WriteableNote;
		this.saveNoteState(answer, RandomQuestionComponent.ComponentIdentifier, newSegment);
	}

	public setSecretionType(secretionId: string, isSkipped: boolean) {
		let newSegment = { value: secretionId, componentIdentifier: DescribeSecretionContainer.ComponentIdentifier, isSkipped } as WriteableNote;
		this.saveNoteState(secretionId, DescribeSecretionContainer.ComponentIdentifier, newSegment);
	}

	public setReportCard(isSkipped: boolean) {
		let newSegment = { value: this.reportCardRatings, componentIdentifier: ReportCardComponent.ComponentIdentifier, isSkipped } as WriteableNote;
		this.saveNoteState(this.reportCardRatings, ReportCardComponent.ComponentIdentifier, newSegment);
	}

	get diaryOwnerNotes$() {
		return this.diaryOwnerNotesSubject.asObservable();
	}

	public sendCreateNoteRequest(): Observable<any> {
		let createNoteRequest = new CreateNoteRequest();

		createNoteRequest.diaryId = this.localStorageHelper.getCurrentDiaryForCurrentNote();
		createNoteRequest.notes = this.localStorageHelper.getCurrentNoteFlowData();

		// Handle skipped edge case for report card. 
		if (this.reportCardSkipped()) createNoteRequest.notes.push(this.setSkippedForReportCard());

		return this.http.post<CreateNoteResponse>(`${this.notesControllerBasePath}`,
			createNoteRequest, this.httpHelper.SetRequiredAwsHttpPostHeaders())
			.pipe(map(res => {
				const newNote = {
					raw: res.readableNote.raw,
					createdOn: res.readableNote.createdOn,
					visits: res.readableNote.visits,
					id: res.readableNote.id
				} as ReadableNote;

				// Save the newly created note id so that we can auto open this note on the view notes page.
				this.localStorageHelper.setRecentlyCreatedNoteId(newNote.id);

				let allNotes = this.diaryOwnerNotesSubject.value.notes

				// If the user hasn't visited the notes yet.
				if (allNotes == undefined || allNotes.length == 0) {
					this.getNotes(createNoteRequest.diaryId).subscribe();
				}
				else {
					// Add the new note to the first element in the array.
					allNotes.unshift(newNote);

					let newState = {
						notes: allNotes,
						title: this.diaryOwnerNotesSubject.value.title,
						avgRating: res.overallRating,
						totalViews: this.diaryOwnerNotesSubject.value.totalViews + newNote.visits
					} as Notes
					this.diaryOwnerNotesSubject.next(newState);
				}
			}));
	}

	public getSongs(): Observable<Song[]> {
		if (this.cachedSongs.length > 0) {
			return of(this.cachedSongs);
		}

		return this.http.get<GetSongsResponse>(`${this.notesControllerBasePath}/songs`)
			.pipe(map(res => { 
				this.cachedSongs = res.songs;
				return this.cachedSongs; 
			}));
	}

	public get songs(): Song[] {
		return this.cachedSongs;
	}

	public getNote(diaryId: string, noteId: string): Observable<GetNoteResponse> {
		return this.http.get<GetNoteResponse>(`${this.notesControllerBasePath}/${diaryId}/${noteId}`)
			.pipe(map(res => { return res; }));
	}

	public deleteNote(noteId: string | null) {
		let accountId = this.signInService.currentAuthenticatedUserValue?.accountId;
		let diaryId = this.signInService.currentAuthenticatedUserValue?.diaryId;

		return this.http.delete(`${this.notesControllerBasePath}/account/${accountId}/diary/${diaryId}/note/${noteId}`, {
			headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Authorization': 'none', 'X-Amz-Date': new Date().toISOString(), })
		}).pipe(map(res => { return res; }))
	}

	// Clear out any note metadata previous to the start of adding a new note.
	public clearNoteRequestData() {
		this.localStorageHelper.removeCurrentDiaryForCurrentNote();
		this.localStorageHelper.removeCurrentNoteFlowData();
		this.painMeterEmojiFileName = null;
		this.selectedSong = null;
		this.selectedHero = null;
		this.selectedSecretionImg = null;
		this.thoughts = null;
		this.selectedQuestionAnswer = null;
		this.cachedQuestions = null;
		this.cachedSongs = [];
		this.selectedQuestionIndex = null;
		this.selectedQuestionId = null;
		this.reportCardRatings = [];
		this.reportCardRatings = [
			new Rating("Ambiance", PottyNotesConstants.ambianceOne, 0),
			new Rating("Cleanliness", PottyNotesConstants.cleanlinessOne, 0),
			new Rating("Toilet Paper", PottyNotesConstants.toiletPaperOne, 0),
			new Rating("Privacy", PottyNotesConstants.privacyOne, 0),
			new Rating("Comfort", PottyNotesConstants.comfortOne, 0),
			new Rating("Overall Rating", null, 0),
		];
		this.reportCardScrollPosition = 0;
		this.totalReportCardRating = 0;
	}

	public recordVisit(noteId: string) {
		return this.http.put(`${this.notesControllerBasePath}/${noteId}/visit`, null);
	}

	public getNotes(diaryId: string) {
		return this.http.get<Notes>(`${this.notesControllerBasePath}/${diaryId}`)
			.pipe(map(res => {
				this.diaryOwnerNotesSubject.next(res);
			}));
	}

	private saveNoteState(value: any, componentIdentifier: number, newNote: WriteableNote) {
		// Determine if we have an existing component in the running list of components.
		let noteData = this.localStorageHelper.getCurrentNoteFlowData();
		let oldSegment = noteData.find(x => x.componentIdentifier == componentIdentifier);
		// Overwrite it if we do.
		if (oldSegment) {
			noteData = noteData.filter(x => x.componentIdentifier != oldSegment.componentIdentifier);
			noteData.push(newNote);
		}
		// Otherwise just add.
		else {
			noteData.push(newNote);
		}

		this.localStorageHelper.setCurrentNoteFlowData(noteData);
	}

	// If every rating is 0, then we assume the user did not fill out the report card (i.e., isSkipped = true).
	// This is because the user cant actually select 0. We just internally default to 0.
	private reportCardSkipped() {
		return this.reportCardRatings.every(x => x.rating == 0);
	}

	// This assumes, the user hasn't triggered saveReportCardMetadata() in the report card component, 
	// so we will add a new segment to the writeable notes with the report card ratings.
	private setSkippedForReportCard() : WriteableNote {
		return {
			componentIdentifier: ReportCardComponent.ComponentIdentifier,
			value: this.reportCardRatings,
			isSkipped: true
		} as WriteableNote
	}
}